// ============================================================
// Auth Context — bootstrap au mount via /auth/refresh
// ============================================================

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import type { User } from '@siverix/shared';
import { api } from '../api';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (emailOrUsername: string, password: string) => Promise<User>;
  /** [2.13.0] Connexion par une assertion du compte Siverix (POST /auth/sso/login). */
  ssoLogin: (assertion: string, issuer: string) => Promise<User>;
  signup: (email: string, username: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.me();
      setUser(r.user);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // tentative refresh au boot pour récupérer la session
    (async () => {
      try {
        await api.me();
      } catch {
        // pas de session → on tente refresh via cookie
      }
      await refresh().finally(() => setLoading(false));
    })();
  }, [refresh]);

  const login = useCallback(async (emailOrUsername: string, password: string) => {
    const r = await api.login({ emailOrUsername, password });
    setUser(r.user);
    return r.user;
  }, []);

  const ssoLogin = useCallback(async (assertion: string, issuer: string) => {
    const r = await api.ssoLogin({ assertion, issuer });
    setUser(r.user);
    return r.user;
  }, []);

  const signup = useCallback(async (email: string, username: string, password: string) => {
    const r = await api.signup({ email, username, password });
    setUser(r.user);
    return r.user;
  }, []);

  const logout = useCallback(async () => {
    await api.logout().catch(() => undefined);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, ssoLogin, signup, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() doit être dans un <AuthProvider>');
  return ctx;
}
